import React, { useId, useState } from 'react';
import './Accordion.scss';

export interface AccordionItem {
  question: string;
  /** Trusted markup from our own content files — never user input. */
  answer: string;
}

interface AccordionProps {
  items: AccordionItem[];
  /** Index of the panel open on first render; omit to start with all closed. */
  defaultOpen?: number;
}

/**
 * Disclosure list used by the FAQs page. Each header is a real <button> so the panels
 * are reachable by keyboard and announced correctly, which the Bootstrap-3 anchor
 * markup on the published site does not manage.
 */
export const Accordion: React.FC<AccordionProps> = ({ items, defaultOpen }) => {
  const [open, setOpen] = useState<number | null>(defaultOpen ?? null);
  const base = useId();

  return (
    <div className="accordion-list">
      {items.map((item, i) => {
        const isOpen = open === i;
        const panelId = `${base}-panel-${i}`;
        const headerId = `${base}-header-${i}`;
        return (
          <div className={`accordion-item ${isOpen ? 'is-open' : ''}`} key={item.question}>
            <h3 className="accordion-heading">
              <button
                type="button"
                id={headerId}
                className="accordion-trigger"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? null : i)}
              >
                <span className="accordion-question">{item.question}</span>
                <i className={`bi ${isOpen ? 'bi-dash-lg' : 'bi-plus-lg'} accordion-marker`}></i>
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={headerId}
              className="accordion-panel"
              hidden={!isOpen}
            >
              <div
                className="accordion-answer legal-doc"
                dangerouslySetInnerHTML={{ __html: item.answer }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
